import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight, MapPin } from "lucide-react";
import { GALLERY } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

function CompareSlider({ project }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  };

  return (
    <div
      ref={ref}
      data-testid="gallery-slider"
      className="relative aspect-[3/2] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-[2rem] shadow-2xl shadow-stone-300/60"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
    >
      <img
        src={project.after}
        alt={`${project.title} — after`}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={project.before}
          alt={`${project.title} — before`}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      </div>
      <span className="font-mono-accent pointer-events-none absolute left-4 top-4 rounded-full bg-slate-900/80 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white backdrop-blur-sm">
        Before
      </span>
      <span className="font-mono-accent pointer-events-none absolute right-4 top-4 rounded-full bg-amber-600/90 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white backdrop-blur-sm">
        After
      </span>
      <div
        data-testid="gallery-handle"
        className="pointer-events-none absolute inset-y-0"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow-lg" />
        <div className="absolute top-1/2 -ml-6 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/90 shadow-xl backdrop-blur-sm">
          <ChevronsLeftRight className="h-5 w-5 text-slate-900" />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [active, setActive] = useState(GALLERY[0]);

  return (
    <section id="work" data-testid="gallery-section" className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-12 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <p className="font-mono-accent mb-4 text-xs uppercase tracking-[0.24em] text-amber-600">
            Recent Work
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Drag to see the{" "}
            <span className="font-serif-accent font-medium italic text-slate-500">difference</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {GALLERY.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`gallery-project-${p.id}`}
              onClick={() => setActive(p)}
              className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-all duration-200 sm:text-sm ${
                active.id === p.id
                  ? "border-slate-900 bg-slate-900 text-white shadow-md"
                  : "border-stone-200 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              {p.title}
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <CompareSlider project={active} />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p data-testid="gallery-project-title" className="font-display text-base font-semibold text-slate-900 sm:text-lg">
                {active.title}
                <span className="ml-3 font-mono-accent text-[11px] uppercase tracking-[0.16em] text-amber-600">
                  {active.service}
                </span>
              </p>
              <p className="flex items-center gap-1.5 text-sm text-slate-500">
                <MapPin className="h-4 w-4 text-amber-600" />
                {active.location}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
